import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Users, FileStack, FolderKanban, UserCog, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";

const ITEMS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, roles: ["administrator", "editor", "reviewer"] },
  { to: "/admin/challenges", label: "Challenge briefs", icon: Inbox, roles: ["administrator", "reviewer"], badge: "challenges_new" },
  { to: "/admin/applications", label: "Applications", icon: Users, roles: ["administrator", "reviewer"], badge: "applications_new" },
  { to: "/admin/projects", label: "OEM projects", icon: FolderKanban, roles: ["administrator", "editor"] },
  { to: "/admin/content", label: "Capabilities", icon: FileStack, roles: ["administrator", "editor"] },
  { to: "/admin/users", label: "Team accounts", icon: UserCog, roles: ["administrator"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!user) return undefined;
    const load = () => api.get("/admin/overview").then(({ data }) => setCounts(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  if (user === null)
    return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-[var(--mineral)]">Checking session…</div>;
  if (user === false) return <Navigate to="/admin/login" state={{ from: location }} replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-[var(--ivory)]">
      <PageMeta title="Admin" />
      <aside className="flex w-60 flex-col border-r border-[var(--line)] bg-[var(--paper)] p-5">
        <div>
          <span className="font-display text-xl font-extrabold text-navy">DBRF</span>
          <p className="font-mono text-[0.6rem] uppercase tracking-widest text-[var(--mineral)]">Admin console</p>
        </div>
        <nav className="mt-8 flex flex-1 flex-col gap-1">
          {ITEMS.filter((i) => i.roles.includes(user.role)).map((i) => {
            const count = i.badge ? counts[i.badge] || 0 : 0;
            return (
              <NavLink key={i.to} to={i.to} end={i.end} data-testid={`admin-nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${isActive ? "bg-[var(--navy)] text-white" : "text-navy hover:bg-[var(--ivory)]"}`}>
                <i.icon size={17} /> <span className="flex-1">{i.label}</span>
                {count > 0 && (
                  <span data-testid={`admin-badge-${i.badge}`} className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-[var(--amber)] px-1.5 py-0.5 font-mono text-[0.66rem] font-bold text-[var(--navy)]">
                    {count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[var(--line)] pt-4">
          <p className="truncate text-sm font-medium text-navy">{user.name}</p>
          <p className="font-mono text-[0.66rem] uppercase tracking-wide text-[var(--cobalt)]">{user.role}</p>
          <button onClick={doLogout} data-testid="admin-logout" className="mt-3 inline-flex items-center gap-2 text-sm text-[var(--mineral)] hover:text-[var(--error)]">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-8">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
}
